import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'motion/react';
import { CloudAlertIcon, ImageOffIcon, InboxIcon, ListFilterIcon, SearchXIcon } from 'lucide-react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { assetUrlsQueryOptions, useUploadAsset } from '@/entities/asset';
import { usePermissions } from '@/entities/user';
import {
  MediaGrid,
  MediaSearchRow,
  MediaTable,
  TagRail,
  folderPath,
  isTagSelected,
  mediaFacetsQueryOptions,
  mediaFoldersQueryOptions,
  previewNormaliseTag,
  tagQueryPairs,
  toggleTag,
  totalItemsInScope,
  useCreateMediaFolder,
  useDeleteBrandMedia,
  useDeleteMediaFolder,
  useFileMediaIntoFolder,
  useIngestBrandMedia,
  useMediaUpload,
  useRemoveTagValue,
  useRenameMediaFolder,
  useRenameTagValue,
  type MediaFolder,
  type TagSelection,
} from '@/entities/brand-media';
import {
  brandFontsQueryOptions,
  fontUrlIds,
  useDeleteBrandFonts,
  useFontReplace,
  useFontUpload,
  BRAND_FONT_QUERY_KEYS,
} from '@/entities/brand-font';
import { DeleteConfirmationModal } from '@/shared/ui';
import { notificationService } from '@/lib/services/notification.service';
import { useDebounce, useLoadFailureToast } from '@/shared/hooks';
import { CollapsibleSection } from '@/shared/ui';
import type { ListBrandMediaQuery } from '@/shared/lib/brand-media';

import {
  allSelected,
  fontIdIn,
  fontKey,
  mediaIdIn,
  mediaKey,
  selectedFontIdSet,
  selectedFontIds,
  selectedMediaIdSet,
  selectedMediaIds,
  toggleAll,
  toggleSelected,
} from '../lib/library-selection';
import { LIBRARY_VIEW_MODE, useLibraryViewMode } from '../model/useLibraryViewMode';
import { useMediaLibrary } from '../model/useMediaLibrary';
import { ApplyTagButton } from './ApplyTagButton';
import { FolderHeader } from './FolderHeader';
import { FolderRail } from './FolderRail';
import { FontCards } from './FontCards';
import { FontDetailPanel } from './FontDetailPanel';
import { FontRows } from './FontRows';
import { LibraryEmpty } from './LibraryEmpty';
import { LibraryViewToggle } from './LibraryViewToggle';
import { MediaDetailPanel } from './MediaDetailPanel';
import { RailHeader } from './RailHeader';
import { SelectionBar } from './SelectionBar';
import { SelectSectionButton } from './SelectSectionButton';
import { TagFilterChips } from './TagFilterChips';
import { TagSelectedButton } from './TagSelectedButton';
import { TagValueMenu } from './TagValueMenu';
import { UploadAssetsSheet } from './UploadAssetsSheet';

/** Matches the inspector's, so a phrase is one search wherever it is typed. */
const SEARCH_DELAY_MS = 300;

/**
 * A named part of this column, ruled off from the one above it.
 *
 * 24px of air on each side of the rule -- a step above the rail's, which breaks tighter because its
 * headings are a rank smaller and its column a third the width. The `mt-3` is the half the column's
 * `gap-3` does not already supply -- the gap is what separates the header and toolbar above, which
 * sit closer together.
 */
const SECTION_DIVIDER_CLASS = 'mt-3 gap-2 border-t border-border-light pt-6';

/**
 * Where the floating selection pill rests, as a pair of margins that cancel to nothing.
 *
 * The top margin drops the (zero-height) layer to the middle of the first section's heading row:
 * the divider's own `mt-3` and hairline and `pt-6`, plus half of that 32px row, less half of the
 * 44px pill. The bottom margin takes the same distance back, along with the column's `gap-3`, so
 * the layer costs the column no height at all and the library sits exactly where it did.
 *
 * Margins rather than a `translate`, which would follow the pill up when it sticks and land it
 * 31px below the toolbar instead of on it. Tied to `SECTION_DIVIDER_CLASS`, which is why it is
 * written beside it: change one and this has to move with it.
 */
const SELECTION_PILL_REST = 'mt-[31px] mb-[-43px]';

// Filter values, not user-facing copy.
/* eslint-disable-next-line i18next/no-literal-string */
const LIBRARY_KIND = { all: 'all', media: 'media', fonts: 'fonts' } as const;

type LibraryKind = (typeof LIBRARY_KIND)[keyof typeof LIBRARY_KIND];

/**
 * The workspace's media, as a place rather than a picker.
 *
 * Takes its scope as a prop and owns only what a person changes while looking: the folder, the
 * search and the selection. A host that already knows the answer to part of the question passes it
 * in, so the same view can sit under a workspace, a project or a brief without being rebuilt.
 *
 * Opening and selecting are two states, because they are two questions. Clicking a card opens it in
 * the detail panel, and clicking it again closes it: one at a time, and the answer to "what am I
 * looking at". Ticking a checkbox puts
 * it in the selection: any number, and the answer to "what will Move and Delete touch". Both at once
 * is normal, and neither disturbs the other -- reading one asset while three are ticked is a thing
 * people do, and a click that quietly emptied the selection would lose work with no way back.
 *
 * The two are held the same way, as keys `library-selection` qualifies with their kind, so the panel
 * is one key beside a set of them. Which of the two panels is showing follows from which kind that
 * key names, which is what keeps them mutually exclusive without a flag saying so.
 */
export const BrandMediaLibrary = ({ scope = {} }: { scope?: ListBrandMediaQuery }) => {
  const { t } = useTranslation('tools');
  const { canUploadBrandAssets } = usePermissions();

  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  /** What the rail is filtered by. Its own question: the toolbar's search narrows the grid instead. */
  const [railSearch, setRailSearch] = useState('');
  const [kind, setKind] = useState<LibraryKind>(LIBRARY_KIND.all);
  /**
   * One selection over both kinds, keyed `media:<id>` / `font:<id>`. See `library-selection`
   * for why the kind travels in the key rather than in a second set.
   */
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  /**
   * What the detail panel is describing, as one key of the same shape. Held here rather than in the
   * grid so it survives the view toggle, the way the selection beside it does.
   */
  const [opened, setOpened] = useState<string | null>(null);
  const [tags, setTags] = useState<TagSelection>({});
  const [uploadSheetOpen, setUploadSheetOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DeleteRequest | null>(null);
  const [pendingFolderDelete, setPendingFolderDelete] = useState<MediaFolder | null>(null);
  /** Which tag value the rail is renaming in place, if any. The rail draws it; what a name means is decided here. */
  const [renamingTag, setRenamingTag] = useState<{ key: string; value: string } | null>(null);
  /** A rename whose target already exists, held until somebody confirms folding the two into one. */
  const [pendingTagMerge, setPendingTagMerge] = useState<{
    key: string;
    from: string;
    to: string;
  } | null>(null);
  const [pendingTagDelete, setPendingTagDelete] = useState<{ key: string; value: string } | null>(
    null,
  );
  const [railHidden, setRailHidden] = useState(false);
  const q = useDebounce(search, SEARCH_DELAY_MS);
  const { viewMode, setViewMode } = useLibraryViewMode();
  const showMedia = kind !== LIBRARY_KIND.fonts;

  const tree = useQuery(mediaFoldersQueryOptions());
  const createFolder = useCreateMediaFolder();
  const fileInto = useFileMediaIntoFolder();
  const renameFolder = useRenameMediaFolder();
  const deleteMedia = useDeleteBrandMedia();
  const deleteFolder = useDeleteMediaFolder();
  const deleteFonts = useDeleteBrandFonts();
  const renameTag = useRenameTagValue();
  const removeTag = useRemoveTagValue();
  const upload = useMediaUpload(useUploadAsset());
  const fontUpload = useFontUpload(useUploadAsset());
  const fontReplace = useFontReplace(useUploadAsset());
  const queryClient = useQueryClient();
  /**
   * Filing and folder deletion both change what the font listing returns, and `brand-font` is a
   * sibling of `brand-media`, which its mutations may not reach into. The host that owns both is
   * where the two caches are kept in step.
   */
  const refetchFonts = () =>
    void queryClient.invalidateQueries({ queryKey: BRAND_FONT_QUERY_KEYS.all });

  const fonts = useQuery(
    brandFontsQueryOptions(
      folderId === undefined ? {} : { folderIds: [folderId], folderScope: 'subtree' as const },
    ),
  );
  // Memoised rather than defaulted at each use: `?? []` is a fresh array every render, which would
  // rebuild the specimens in `FontCards` and re-register every font face with the document.
  const fontRows = useMemo(() => fonts.data ?? [], [fonts.data]);
  // The same key `FontCards` and `FontRows` use for the same ids, so this is their query rather
  // than a second request: the panel needs one url out of the batch they already resolve.
  const fontUrls = useQuery(assetUrlsQueryOptions(fontUrlIds(fontRows)));

  const tagPairs = tagQueryPairs(tags);
  // Tags describe assets; a font carries none. Under a chosen tag the section would list every font
  // there is, which reads as the filter having failed to reach them rather than as their not being
  // the kind of thing it asks about.
  const showFonts = kind !== LIBRARY_KIND.media && tagPairs.length === 0;

  const railQuery = {
    ...scope,
    ...(q.length > 0 ? { q } : {}),
    ...(tagPairs.length > 0 ? { tags: tagPairs } : {}),
  };
  const query = {
    ...railQuery,
    // Subtree rather than direct: standing in a folder is about to mean its whole subtree, and a
    // parent whose grid showed less than its own rollup count would read as a bug.
    // eslint-disable-next-line i18next/no-literal-string -- a wire enum value, not copy
    ...(folderId !== undefined ? { folderIds: [folderId], folderScope: 'subtree' as const } : {}),
  };
  // Resolved ahead of the hook call, since the hook uses it to decide which one row asks for a
  // bigger derivative than the grid does.
  const openedMediaId = mediaIdIn(opened);
  const library = useMediaLibrary(query, openedMediaId);
  /**
   * The grid's question minus the folder: the tags one folder happens to hold are not the tags there
   * are, and a rail that emptied on stepping into an untagged folder reads as the filters having been
   * taken away rather than as that folder being untagged.
   *
   * It does carry the current tags: the server drops only the counted facet's own selection from its
   * own counts and keeps every other facet's, so a rail built from a tagless query would compute
   * every count as though nothing were selected.
   */
  const facets = useQuery(mediaFacetsQueryOptions(railQuery));

  const { refetch: refetchFontsQuery } = fonts;
  const retryFonts = useCallback(() => void refetchFontsQuery(), [refetchFontsQuery]);

  // The two listings fail separately and are told separately: one of them coming back is a
  // different piece of news from both of them going, and a single toast covering either would
  // leave a person guessing which half of the page they are looking at.
  useLoadFailureToast({
    isError: library.isError,
    message: t('brandMedia.failed'),
    retryLabel: t('brandMedia.retry'),
    onRetry: library.retry,
  });
  useLoadFailureToast({
    isError: fonts.isError,
    message: t('brandFont.failed'),
    retryLabel: t('brandMedia.retry'),
    onRetry: retryFonts,
  });

  const folders = tree.data?.folders ?? [];

  // Toasted here rather than in the mutation, for the reason the deletes above give: the wording
  // belongs to the surface. What it can honestly say is that the work was queued -- the tags land on
  // a later fetch, which the listing's own poll picks up.
  const retryTagging = useIngestBrandMedia();
  const retryTaggingWithFeedback = {
    // The count the server took on, not the one asset the retry was started from: the endpoint works
    // through everything outstanding, and saying "1" would misreport what is about to happen.
    onSuccess: ({ started }: { started: number }) =>
      notificationService.showSuccess(t('brandMedia.taggingRetried', { count: started })),
    onError: (error: Error) =>
      notificationService.showError(error.message || t('brandMedia.taggingRetryFailed')),
  };

  const chosenMediaIds = selectedMediaIdSet(selectedIds);
  const chosenFontIds = selectedFontIdSet(selectedIds);
  const selectedRows = library.rows.filter((row) => chosenMediaIds.has(row.id));

  /**
   * Whether a per-asset tag write would be building on rows the library has already moved past.
   *
   * The selection controls send each asset's whole list for one key, rebuilt from the row in hand,
   * so a row read before a library-wide rename or removal would put the old value back while
   * adding an unrelated one. Both of those invalidate the listing, but the gap between the request
   * going out and the fresh rows arriving is long enough to click in, so the controls are held
   * from the moment one starts until the listing has caught up rather than only while it is
   * in flight.
   */
  const tagWritesStale = renameTag.isPending || removeTag.isPending || library.isStale;

  // Resolved against the rows on screen rather than trusted: a filter or a move can take the open
  // row out of the listing, and the panel closing is the honest answer to describing something that
  // is no longer here.
  const openedRow =
    openedMediaId === undefined ? undefined : library.rows.find((row) => row.id === openedMediaId);
  // The library's own preview-sized tile for this row, not the grid's thumb-sized one: the still
  // and the clip it carries are what the detail panel and the enlarged view draw.
  const openedTile = library.openedTile;

  const openedFontId = fontIdIn(opened);
  const openedFont =
    openedFontId === undefined ? undefined : fontRows.find((font) => font.id === openedFontId);

  /**
   * Clicking the card that is already open closes the panel, rather than reopening what is
   * already there. The card is the only handle on the panel a person's eye is on when they want
   * it gone, and a click that did nothing would read as the card having stopped responding.
   */
  const toggleOpen = (key: string) => setOpened((current) => (current === key ? null : key));

  // A choice carries the asset id a tile would bind, and selection is keyed by media id, so the
  // click is resolved back to its tile before anything is opened.
  const open = (assetId: string) => {
    const tile = library.tiles.find((entry) => entry.assetId === assetId);
    if (tile !== undefined) toggleOpen(mediaKey(tile.id));
  };

  const setOpenedFont = (fontId: string) => toggleOpen(fontKey(fontId));

  const toggleMedia = (mediaId: string) =>
    setSelectedIds((current) => toggleSelected(current, mediaKey(mediaId)));

  const toggleFont = (fontId: string) =>
    setSelectedIds((current) => toggleSelected(current, fontKey(fontId)));

  /**
   * What each section is drawing, as selection keys.
   *
   * They follow the filters rather than the library: a person looking at one folder under one tag
   * means those, not every asset there is. A section the kind filter has hidden, or one a failed
   * request left with nothing in it, is empty here because it is not on screen to be selected.
   *
   * Held per section rather than as one list, because the two are what the section headers offer to
   * take in one go: "every font" is a question a person asks without meaning the photographs too.
   */
  const mediaKeys = useMemo(
    () => (showMedia && !library.isError ? library.tiles.map((tile) => mediaKey(tile.id)) : []),
    [showMedia, library.isError, library.tiles],
  );
  const fontKeys = useMemo(
    () => (showFonts && !fonts.isError ? fontRows.map((font) => fontKey(font.id)) : []),
    [showFonts, fonts.isError, fontRows],
  );
  /** Both sections at once, which is what the bar's checkbox reaches. */
  const shownKeys = useMemo(() => [...mediaKeys, ...fontKeys], [mediaKeys, fontKeys]);

  const allShownSelected = allSelected(selectedIds, shownKeys);

  // Only the keys the control stands over come back out, not the whole selection: something ticked
  // before the filter narrowed is still ticked, and emptying the set would delete a choice this
  // control never showed.
  const toggleKeys = (keys: readonly string[]) => () =>
    setSelectedIds((current) => toggleAll(current, keys));

  /**
   * Files whatever is selected, of either kind, in one call.
   *
   * One request rather than one per kind: the person made a single move, and two requests could
   * half-succeed and leave their selection split across two folders.
   */
  const fileSelection = (destination: string | null) => {
    const mediaIds = selectedMediaIds(selectedIds);
    const fontIds = selectedFontIds(selectedIds);

    fileInto.mutate(
      {
        ...(mediaIds.length > 0 ? { mediaIds } : {}),
        ...(fontIds.length > 0 ? { fontIds } : {}),
        folderId: destination,
      },
      { onSuccess: refetchFonts },
    );
    setSelectedIds(new Set());
  };

  const confirmDelete = () => {
    if (pendingDelete === null) return;
    const { mediaIds, fontIds, surface } = pendingDelete;
    // What was asked for, not the `archived` counts: those are lower when another tab already
    // retired a row, and "0 assets deleted" after a successful confirm reads as a bug. Read before
    // the mutation, since the selection is cleared on the way out.
    const asked = mediaIds.length + fontIds.length;

    // Two endpoints, started together rather than one after the other: a selection spanning both
    // kinds is one gesture, and chaining them would make it twice as slow to answer.
    void Promise.all([
      mediaIds.length > 0 ? deleteMedia.mutateAsync({ mediaIds, surface }) : undefined,
      fontIds.length > 0 ? deleteFonts.mutateAsync({ fontIds }) : undefined,
    ])
      .then(() => {
        notificationService.showSuccess(
          mediaIds.length === 0
            ? t('brandFont.deletedCount', { count: asked })
            : fontIds.length === 0
              ? t('brandMedia.deleted', { count: asked })
              : t('brandMedia.deletedItems', { count: asked }),
        );
        setPendingDelete(null);
        setSelectedIds(new Set());
        // The panel too, whether it was the thing deleted or one of a batch that included it: it
        // would otherwise be left describing a row that no longer exists.
        setOpened(null);
      })
      .catch((error: unknown) => {
        const reason = error instanceof Error ? error.message : '';
        notificationService.showError(reason || t('brandMedia.deleteFailed'));
        // The selection survives: whichever half failed is still there to try again.
        setPendingDelete(null);
      });
  };

  /**
   * Sends a rename, and drops the filter if it was standing on the value that just changed name.
   *
   * Without that the grid would keep asking for a value nothing carries any more, and answer with
   * an empty library a moment after a rename that worked.
   */
  const commitTagRename = (key: string, from: string, to: string, merged: boolean) => {
    renameTag.mutate(
      { key, from, to, merged },
      {
        onSuccess: ({ updated }) => {
          notificationService.showSuccess(
            merged
              ? t('brandMedia.tagMerged', { from, to, count: updated })
              : t('brandMedia.tagRenamed', { to, count: updated }),
          );
          setTags((current) =>
            isTagSelected(current, key, from)
              ? toggleTag(toggleTag(current, key, from), key, to)
              : current,
          );
          setPendingTagMerge(null);
        },
        onError: (error) => {
          notificationService.showError(error.message || t('brandMedia.tagRenameFailed'));
          setPendingTagMerge(null);
        },
      },
    );
  };

  /**
   * What a committed inline rename means, which the rail cannot decide for itself.
   *
   * A name already in use under this key is a merge, and a merge cannot be undone, so it stops
   * here for a confirmation instead of going straight out. Everything else is the folder rail's
   * own rule: a rename to the same words, or to nothing, is not a rename.
   */
  const handleTagRenameCommit = (key: string, from: string, typed: string) => {
    setRenamingTag(null);
    const to = previewNormaliseTag(typed);
    if (to === '' || to === from) return;

    const existing = facets.data?.find((facet) => facet.key === key);
    if (existing?.values.some((entry) => entry.value === to)) {
      setPendingTagMerge({ key, from, to });
      return;
    }

    commitTagRename(key, from, to, false);
  };

  const confirmTagDelete = () => {
    if (pendingTagDelete === null) return;
    const { key, value } = pendingTagDelete;

    removeTag.mutate(
      { key, value },
      {
        onSuccess: ({ updated }) => {
          notificationService.showSuccess(t('brandMedia.tagDeleted', { value, count: updated }));
          // The rail row it was filtering on has just stopped existing.
          setTags((current) =>
            isTagSelected(current, key, value) ? toggleTag(current, key, value) : current,
          );
          setPendingTagDelete(null);
        },
        onError: (error) => {
          notificationService.showError(error.message || t('brandMedia.tagDeleteFailed'));
          setPendingTagDelete(null);
        },
      },
    );
  };

  const confirmFolderDelete = () => {
    if (pendingFolderDelete === null) return;
    const { id, name } = pendingFolderDelete;

    deleteFolder.mutate(
      { folderId: id },
      {
        onSuccess: ({ archivedMedia, archivedFonts }) => {
          // Both kinds in one number, the way the rail counts a folder: what went is what was
          // filed there, and splitting it into "3 assets and 1 font" would be a more detailed
          // answer to a question nobody asked.
          const archived = archivedMedia + archivedFonts;
          notificationService.showSuccess(
            archived === 0
              ? t('brandMedia.folderDeletedEmpty', { name })
              : t('brandMedia.folderDeleted', { name, count: archived }),
          );
          refetchFonts();
          setPendingFolderDelete(null);
          // The grid would otherwise keep filtering on a folder that no longer exists, and show
          // nothing at all rather than the library it fell back to.
          if (folderId === id) setFolderId(undefined);
        },
        onError: (error) => {
          notificationService.showError(error.message || t('brandMedia.folderDeleteFailed'));
          setPendingFolderDelete(null);
        },
      },
    );
  };

  // Narrowing of any kind, not just the search box: a folder, a tag or the kind filter can empty
  // the grid just as well, and "nothing here yet" would be a lie in front of a library that is
  // merely filtered.
  const narrowed =
    q.length > 0 || tagPairs.length > 0 || folderId !== undefined || kind !== LIBRARY_KIND.all;
  const settled = !library.isLoading && !library.isError && !fonts.isLoading && !fonts.isError;
  const holdsSomething = library.tiles.length > 0 || fontRows.length > 0;
  // Keyed on there being nothing to draw at all rather than on the library being empty, so a search
  // that matched nothing collapses to one notice too instead of pairing it with an empty fonts
  // section the search never even applied to. A section the filters have hidden counts as nothing
  // here for the same reason: it is not on the page to speak for itself.
  const nothingToShow =
    settled && (!showMedia || library.tiles.length === 0) && (!showFonts || fontRows.length === 0);
  const narrowedTitle =
    q.length > 0
      ? t('brandMedia.noMatchesTitle', { search: q })
      : t('brandMedia.noFilterMatchesTitle');

  // Undefined while there is a grid to draw, so the caller reads as "a message, or the grid".
  const noMediaMessage =
    nothingToShow || library.isLoading || library.isError || library.tiles.length > 0
      ? undefined
      : narrowed
        ? narrowedTitle
        : t('brandMedia.noMedia');

  /**
   * How the confirmation names what it is about to delete.
   *
   * One thing is named rather than counted: somebody deleting a single asset or font is owed its
   * title. Several become a count, and a count spanning both kinds counts items, because neither
   * "assets" nor "fonts" would be true of the other half.
   */
  const deleteNaming = (request: DeleteRequest) => {
    const { mediaIds, fontIds } = request;
    const count = mediaIds.length + fontIds.length;

    if (count === 1) {
      if (mediaIds.length === 1) {
        return {
          resourceName:
            library.rows.find((row) => row.id === mediaIds[0])?.title ?? t('brandMedia.thisAsset'),
        };
      }
      const font = fontRows.find((row) => row.id === fontIds[0]);
      return {
        resourceName:
          font === undefined ? t('brandFont.thisFont') : (font.familyName ?? font.filename),
      };
    }

    if (mediaIds.length === 0) {
      return { customModalTitle: t('brandFont.deleteSelectedTitle', { count }) };
    }
    if (fontIds.length === 0) {
      return { customModalTitle: t('brandMedia.deleteSelectedTitle', { count }) };
    }
    return { customModalTitle: t('brandMedia.deleteSelectedItemsTitle', { count }) };
  };

  /**
   * A font's warning talks about the pieces that already set type in it, an asset's about the
   * boards that place it. A mixed selection gets the asset wording, which is the closer of the two
   * to being true of both.
   */
  const deleteWarning = ({ mediaIds, fontIds }: DeleteRequest) =>
    mediaIds.length === 0
      ? t('brandFont.deleteWarning', { count: fontIds.length })
      : t('brandMedia.deleteWarning', { count: mediaIds.length + fontIds.length });

  return (
    // Two surfaces with air between them rather than one box divided by a rule: the rail narrows
    // what the column shows, so they are two things that talk, not two halves of one. The gap over
    // the page's warm grey is what separates them, so neither carries a border -- which is also
    // what keeps the detail panel from reading as a third division of the same card.
    // The space between the three is a margin on each rather than a `gap` on this row, because the
    // detail panel animates its own margin open as it reveals -- a container gap would add to it,
    // and a gap cannot be animated, so it also cannot be the panel's.
    <div className="flex min-h-0 flex-1 text-foreground">
      {/* Collapsed by width rather than unmounted, so hiding the rail is one movement instead of a
          jump. The panel inside keeps its own width while the wrapper closes over it, which is what
          stops the rail reflowing its contents on the way out. `inert` because a rail nobody can
          see is still in the tab order otherwise. `min-w-0` is load-bearing: a flex item's default
          `min-width: auto` floors it at the 300px panel inside, and `w-0` never takes. */}
      <div
        inert={railHidden}
        data-testid="brand-media-rail"
        className={`min-h-0 min-w-0 shrink-0 overflow-hidden transition-[width] duration-base ease-out ${
          railHidden ? 'w-0' : 'w-[300px]'
        }`}
      >
        <div className="flex h-full min-h-0 w-[300px] flex-col rounded-lg bg-card">
          {/* One field over both rails rather than one per section: a folder and a tag are two ways
              of asking where something is, and two boxes side by side would make finding a word a
              choice about which of them to type it into. */}
          <RailHeader
            title={t('brandMedia.railTitle')}
            search={railSearch}
            onSearchChange={setRailSearch}
          />

          {/* The header stays while this scrolls, which is the whole reason the padding moved off
              the column and onto the two of them: the rule under the title has to reach both edges. */}
          <div className="flex min-h-0 flex-1 flex-col gap-md overflow-y-auto p-6">
            <FolderRail
              folders={folders}
              total={library.tiles.length}
              selectedFolderId={folderId}
              search={railSearch}
              onSelect={setFolderId}
              onCreate={(name) => createFolder.mutate({ name })}
              onCreateChild={(parentId, name) => createFolder.mutate({ name, parentId })}
              onRename={(folderId, name) => renameFolder.mutate({ folderId, name })}
              onDelete={setPendingFolderDelete}
              isCreating={createFolder.isPending}
            />

            {/* Even air on both sides of the rule, matching the gap above it, so the two groups
                read as siblings rather than as one heading trailing a long way behind its list. */}
            <div className="shrink-0 border-t border-border-light pt-md">
              <TagRail
                facets={facets.data ?? []}
                selection={tags}
                itemsInScope={totalItemsInScope(facets.data ?? [], library.tiles.length)}
                search={railSearch}
                action={<TagSelectedButton rows={selectedRows} stale={tagWritesStale} />}
                renaming={renamingTag ?? undefined}
                onRenameCommit={handleTagRenameCommit}
                onRenameCancel={() => setRenamingTag(null)}
                // Two controls on a row, and they answer different questions. The apply button acts
                // on the grid's selection, so it appears only while something is chosen: with an
                // empty selection there is nothing for it to act on, and a disabled one repeated
                // down every row is noise. The menu acts on the library, so it is always there --
                // on every key, including the ones image analysis wrote, which in a real library
                // is all of them.
                valueAction={(key, value) => (
                  <>
                    {selectedRows.length > 0 && (
                      <ApplyTagButton
                        tagKey={key}
                        value={value}
                        rows={selectedRows}
                        stale={tagWritesStale}
                      />
                    )}
                    <TagValueMenu
                      tagKey={key}
                      value={value}
                      onRename={() => setRenamingTag({ key, value })}
                      onDelete={() => setPendingTagDelete({ key, value })}
                    />
                  </>
                )}
                onToggle={(key, value) => setTags((current) => toggleTag(current, key, value))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* The margin is on this column rather than a `gap` on the row for the reason above, and it
          has to go with the rail: left against the page edge, a hidden rail would otherwise leave a
          strip of nothing where it used to be. Same duration and easing as the rail, so the two
          close as one edge rather than as a panel and a gap.
          `app-inset` is the step the page already puts between its header and this row, so the three
          surfaces sit the same distance apart across as they do down. */}
      <section
        className={`flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto rounded-lg bg-card px-xl pb-xl pt-md transition-[margin-left] duration-base ease-out ${
          railHidden ? 'ml-0' : 'ml-app-inset'
        }`}
      >
        {/* Not sticky, unlike the toolbar under it: the toolbar is controls, which have to stay
            reachable, and this is context, which is only worth the height while you are at the top
            of the list you just arrived in. */}
        <FolderHeader
          path={folderPath(folders, folderId)}
          filters={
            <TagFilterChips
              selection={tags}
              onRemove={(key, value) => setTags((current) => toggleTag(current, key, value))}
            />
          }
          railHidden={railHidden}
          onToggleRail={() => setRailHidden((hidden) => !hidden)}
          onNavigate={(folder) => setFolderId(folder.id)}
          onRename={(folder, name) => renameFolder.mutate({ folderId: folder.id, name })}
          onDelete={setPendingFolderDelete}
        />

        {/* Sticky, because a control nobody can reach is not a control: the row scrolls out of
            sight exactly when the library is long enough for the fonts below it to be worth
            finding, or for a selection to be worth acting on.
            A negative offset rather than `top-0`: a sticky offset is measured from the scroll
            container's padding box, so `top-0` pins the row the column's top padding low and
            leaves a strip of scrolling tiles showing above it. This has to track that padding,
            and the horizontal inset has to track the side padding, for the same reason. Written
            numerically because the named steps have no negative form. */}
        <div className="sticky -top-4 z-10 -mx-6 flex flex-col gap-2 bg-card px-xl py-2">
          <MediaSearchRow
            toolbar
            search={search}
            onSearchChange={setSearch}
            // Assets rather than media: fonts are in this box's reach too, and a placeholder that
            // named only the pictures would promise less than the search does.
            placeholder={t('brandMedia.searchAssets')}
            // A single modal listing every accepted format (images, video, fonts) rather than the
            // native OS picker, so a person never has to know in advance which upload flow a file
            // belongs to.
            onOpenUpload={() => canUploadBrandAssets && setUploadSheetOpen(true)}
            uploadDisabledReason={
              canUploadBrandAssets ? undefined : t('brandMediaLibrary.permissions.noPermission')
            }
          >
            {/* Gated on the library holding something at all rather than on what the filter has
                left: hiding it whenever the column is empty would take away the only way back out
                of a kind that has nothing in it. */}
            {holdsSomething && (
              <Select value={kind} onValueChange={(value) => setKind(value as LibraryKind)}>
                {/* The selected word alone ("All") says nothing about what it selects, sitting next
                    to a search box that could just as well be filtered by anything. The prefix
                    names the axis, and the width follows the content so the longest option is not
                    clipped by a fixed one. */}
                <SelectTrigger
                  className="w-auto justify-start gap-1.5"
                  aria-label={t('brandMedia.kindFilter')}
                >
                  <ListFilterIcon className="text-foreground-alt" />
                  <span className="text-foreground-alt">{t('brandMedia.kindFilterPrefix')}</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={LIBRARY_KIND.all}>{t('brandMedia.kindAll')}</SelectItem>
                  <SelectItem value={LIBRARY_KIND.media}>{t('brandMedia.kindMedia')}</SelectItem>
                  <SelectItem value={LIBRARY_KIND.fonts}>{t('brandMedia.kindFonts')}</SelectItem>
                </SelectContent>
              </Select>
            )}

            <div className="ml-auto">
              <LibraryViewToggle value={viewMode} onChange={setViewMode} />
            </div>
          </MediaSearchRow>
        </div>

        {/* From the first tick, not the second. Ticking one no longer opens anything, so there is
            no panel beside it carrying the move and the delete -- without the bar, a single tick
            would be a selection nothing could act on.
            Assets and fonts are counted and acted on together here: they are one selection, and
            a bar that could only move half of it would make the person do the same move twice.

            A layer of its own rather than a second row inside the toolbar. Zero height and a
            negative margin that cancels its share of the column's `gap-3` keep it out of the flow
            entirely, so the library neither shifts down when a pill arrives nor back up when it
            leaves -- the grid stays put while you pick across it.

            Sticky at the same offset as the toolbar and a layer above it, so scrolling parks the
            pill over the upload button. That button is the one control a selection has no use for,
            which is what makes it the right thing to cover.

            It has to cover it whole, hence the nudge past the column's right edge. Flush, the
            pill's own radius curves away from the button's squarer corners and leaves a sliver of
            it showing down the right side, which reads as a rendering fault rather than as one
            thing over another. The sliver is at its widest just past the button's own corners, at
            4.2px; six clears it without the pill visibly leaving the column's edge.

            The layer cannot take pointer events: it spans the column's full width at the pill's
            height, and a transparent strip that swallowed clicks would make the tiles under it
            unpickable. The pill itself takes them back. */}
        <div
          className={`pointer-events-none sticky -top-3 z-20 -mr-1.5 flex h-0 items-start justify-end ${SELECTION_PILL_REST}`}
        >
          <AnimatePresence>
            {selectedIds.size > 0 && (
              <SelectionBar
                count={selectedIds.size}
                allShownSelected={allShownSelected}
                folders={folders}
                onToggleAllShown={toggleKeys(shownKeys)}
                onClear={() => setSelectedIds(new Set())}
                onMove={fileSelection}
                onDelete={() =>
                  setPendingDelete({
                    mediaIds: selectedMediaIds(selectedIds),
                    fontIds: selectedFontIds(selectedIds),
                    surface: 'bulk_bar',
                  })
                }
              />
            )}
          </AnimatePresence>
        </div>

        {library.isLoading && (
          <p className="text-body-md text-foreground-alt">{t('brandMedia.loading')}</p>
        )}

        {nothingToShow ? (
          // One state for the whole column rather than one per section: two separate notices,
          // each counting its own kind of nothing, describe a place that is more absent than it
          // is. There is no action here because the row above already carries the only one.
          <LibraryEmpty
            // Stretched so the notice sits in the middle of the space it is explaining rather than
            // pinned under the toolbar.
            className="flex-1"
            // Not the `ImagesIcon` the page header and the hub tile already carry: repeated here it
            // only says "media" a third time, which is the one thing a person on this page knows.
            // An empty tray says the part they came to find out.
            icon={narrowed ? <SearchXIcon aria-hidden /> : <InboxIcon aria-hidden />}
            title={narrowed ? narrowedTitle : t('brandMedia.emptyTitle')}
            description={
              narrowed ? t('brandMedia.narrowedDescription') : t('brandMedia.emptyDescription')
            }
            descriptionMaxWidth="42ch"
            data-testid="brand-media-empty"
          />
        ) : (
          <>
            {/* Both views are handed the same selection and the same open key, and neither keeps a
                copy, so switching between them cannot change what is ticked or what is open. */}
            {/* The failure stands in place of the section rather than inside it: a heading counting
                the nothing a failed request returned reads as a library that emptied, which is the
                one thing that did not happen. */}
            {showMedia && library.isError ? (
              <LibraryEmpty
                className="flex-1"
                role="alert"
                icon={<CloudAlertIcon aria-hidden />}
                title={t('brandMedia.failed')}
                description={t('brandMedia.failedDescription')}
                descriptionMaxWidth="42ch"
                data-testid="brand-media-error"
              >
                <Button variant="outline" size="sm" onClick={library.retry}>
                  {t('brandMedia.retry')}
                </Button>
              </LibraryEmpty>
            ) : null}

            {showMedia && !library.isError && (
              <CollapsibleSection
                data-testid="brand-media-media-section"
                className={SECTION_DIVIDER_CLASS}
                title={t('brandMedia.mediaSection', { count: library.tiles.length })}
                action={
                  <SelectSectionButton
                    label={t('brandMedia.selectAllMedia')}
                    clearLabel={t('brandMedia.deselectAllMedia')}
                    keys={mediaKeys}
                    selection={selectedIds}
                    onToggle={toggleKeys(mediaKeys)}
                  />
                }
              >
                {noMediaMessage !== undefined ? (
                  // Padded half the notice above it, because this one is a section heading's worth
                  // of absence with the fonts section still to come underneath: the default 48px
                  // would push those off the fold to say one line.
                  <LibraryEmpty
                    className="md:p-6"
                    // `ImageOff` rather than the tray above: this notice runs with a fonts section
                    // underneath it, so which kind is missing is the useful half of what it says.
                    icon={narrowed ? <SearchXIcon aria-hidden /> : <ImageOffIcon aria-hidden />}
                    title={noMediaMessage}
                    data-testid="brand-media-no-media"
                  />
                ) : viewMode === LIBRARY_VIEW_MODE.cards ? (
                  <MediaGrid
                    tiles={library.tiles}
                    columns="fill"
                    selectedIds={chosenMediaIds}
                    openMediaId={openedMediaId}
                    onChoose={open}
                    onToggleSelect={toggleMedia}
                    // The same reading Content Studio's assets tab draws over its previews:
                    // browsing a folder is exactly when how a shot could function in a layout is
                    // worth seeing.
                    showAdUsage
                  />
                ) : (
                  <MediaTable
                    tiles={library.tiles}
                    selectedIds={chosenMediaIds}
                    openMediaId={openedMediaId}
                    onChoose={open}
                    onToggleSelect={toggleMedia}
                  />
                )}
              </CollapsibleSection>
            )}

            {/* Fonts share this one library rather than a tab of their own: the same upload button
                above adds either kind, so there is nowhere else a font would belong. */}
            {showFonts && fonts.isError ? (
              <LibraryEmpty
                // Padded the way the section-sized notices above are: this one sits under whatever
                // the media half resolved to, so it is a band on the page rather than the whole of it.
                className={`${SECTION_DIVIDER_CLASS} md:p-6`}
                role="alert"
                icon={<CloudAlertIcon aria-hidden />}
                title={t('brandFont.failed')}
                description={t('brandFont.failedDescription')}
                descriptionMaxWidth="42ch"
                data-testid="brand-font-error"
              >
                <Button variant="outline" size="sm" onClick={retryFonts}>
                  {t('brandMedia.retry')}
                </Button>
              </LibraryEmpty>
            ) : null}

            {showFonts && !fonts.isError && (
              <CollapsibleSection
                data-testid="brand-media-fonts-section"
                className={SECTION_DIVIDER_CLASS}
                title={t('brandFont.sectionTitle', { count: fontRows.length })}
                action={
                  <SelectSectionButton
                    label={t('brandFont.selectAllFonts')}
                    clearLabel={t('brandFont.deselectAllFonts')}
                    keys={fontKeys}
                    selection={selectedIds}
                    onToggle={toggleKeys(fontKeys)}
                  />
                }
              >
                {fonts.isLoading && (
                  <p className="text-body-md text-foreground-alt">{t('brandFont.loading')}</p>
                )}
                {!fonts.isLoading &&
                  (viewMode === LIBRARY_VIEW_MODE.cards ? (
                    // Specimens beside the asset cards: a card view whose fonts were a table would
                    // say the fonts are a different kind of thing than the pictures, which they
                    // are not.
                    <FontCards
                      fonts={fontRows}
                      selectedIds={chosenFontIds}
                      openFontId={openedFontId}
                      onChoose={setOpenedFont}
                      onToggleSelect={toggleFont}
                    />
                  ) : (
                    <FontRows
                      fonts={fontRows}
                      selectedIds={chosenFontIds}
                      openFontId={openedFontId}
                      onChoose={setOpenedFont}
                      onToggleSelect={toggleFont}
                    />
                  ))}
              </CollapsibleSection>
            )}
          </>
        )}
      </section>

      <UploadAssetsSheet
        open={uploadSheetOpen && canUploadBrandAssets}
        onClose={() => setUploadSheetOpen(false)}
        mediaUpload={{ ...upload, upload: (files) => upload.upload(files, { folderId }) }}
        fontUpload={fontUpload}
      />

      {pendingDelete !== null && (
        <DeleteConfirmationModal
          open
          data-testid="brand-media-delete-dialog"
          isDeleting={deleteMedia.isPending || deleteFonts.isPending}
          onCancel={() => setPendingDelete(null)}
          onConfirmDelete={confirmDelete}
          {...deleteNaming(pendingDelete)}
          warningMessage={deleteWarning(pendingDelete)}
        />
      )}

      {pendingFolderDelete !== null && (
        <DeleteConfirmationModal
          open
          data-testid="brand-media-folder-delete-dialog"
          isDeleting={deleteFolder.isPending}
          onCancel={() => setPendingFolderDelete(null)}
          onConfirmDelete={confirmFolderDelete}
          resourceName={pendingFolderDelete.name}
          // The rollup the rail already shows beside the folder, so the question promises exactly
          // the number the person was looking at when they asked it.
          warningMessage={
            pendingFolderDelete.subtreeCount === 0
              ? t('brandMedia.deleteEmptyFolderWarning')
              : t('brandMedia.deleteFolderWarning', { count: pendingFolderDelete.subtreeCount })
          }
        />
      )}

      {pendingTagMerge !== null && (
        <DeleteConfirmationModal
          open
          data-testid="brand-media-tag-merge-dialog"
          isDeleting={renameTag.isPending}
          onCancel={() => setPendingTagMerge(null)}
          onConfirmDelete={() =>
            commitTagRename(pendingTagMerge.key, pendingTagMerge.from, pendingTagMerge.to, true)
          }
          customModalTitle={t('brandMedia.mergeTagTitle', { to: pendingTagMerge.to })}
          warningMessage={t('brandMedia.mergeTagWarning', {
            from: pendingTagMerge.from,
            to: pendingTagMerge.to,
          })}
          confirmLabel={t('brandMedia.mergeTagConfirm')}
          pendingLabel={t('brandMedia.mergeTagPending')}
        />
      )}

      {pendingTagDelete !== null && (
        <DeleteConfirmationModal
          open
          data-testid="brand-media-tag-delete-dialog"
          isDeleting={removeTag.isPending}
          onCancel={() => setPendingTagDelete(null)}
          onConfirmDelete={confirmTagDelete}
          resourceName={pendingTagDelete.value}
          // No count. The rail's own number is scoped to the folder and filters in force, where this
          // reaches the whole workspace, so quoting it would promise the wrong figure in both
          // directions. What actually changed is reported in the toast afterwards.
          warningMessage={t('brandMedia.deleteTagWarning', { value: pendingTagDelete.value })}
        />
      )}

      {/* One constant key across every asset, so opening a different tile swaps the panel's contents
          in place. Only opening and closing crosses AnimatePresence.
          The two panels are mutually exclusive by construction: one key is open, and its prefix
          decides which of them reads it. */}
      <AnimatePresence initial={false}>
        {openedRow !== undefined && (
          <MediaDetailPanel
            key="media-detail-panel"
            row={openedRow}
            url={openedTile?.url}
            videoUrl={openedTile?.videoUrl}
            // Read off the tile rather than recomputed here, so the panel and the grid badge behind it
            // can never disagree about whether this asset is still being catalogued.
            ingestion={openedTile?.ingestion}
            folders={folders}
            isRetryingTagging={retryTagging.isPending}
            onRetryTagging={() =>
              retryTagging.mutate({ mediaId: openedRow.id }, retryTaggingWithFeedback)
            }
            onClose={() => setOpened(null)}
            // The panel stays open after a move, unlike the bulk bar's, which is acting on a
            // selection it then clears: this one is describing an asset, and closing it out from
            // under the person who just filed it would take away the confirmation that anything
            // happened. A move that takes the row out of the folder being browsed closes it anyway,
            // since the open key is resolved against the rows on screen.
            onMove={(destination) =>
              fileInto.mutate({ mediaIds: [openedRow.id], folderId: destination })
            }
            onRequestDelete={() =>
              setPendingDelete({
                mediaIds: [openedRow.id],
                fontIds: [],
                surface: 'detail_panel',
              })
            }
          />
        )}

        {openedFont !== undefined && (
          <FontDetailPanel
            key="font-detail-panel"
            font={openedFont}
            {...(fontUrls.data?.[openedFont.assetId] !== undefined
              ? { url: fontUrls.data[openedFont.assetId] }
              : {})}
            folders={folders}
            onClose={() => setOpened(null)}
            // Stays open after a move, for the reason the asset panel gives.
            onMove={(destination) =>
              fileInto.mutate(
                { fontIds: [openedFont.id], folderId: destination },
                {
                  onSuccess: refetchFonts,
                },
              )
            }
            onReplace={(file) =>
              void fontReplace
                .replace(openedFont.id, file)
                .then(() => notificationService.showSuccess(t('brandFont.replaced')))
                .catch((error: unknown) =>
                  notificationService.showError(
                    (error instanceof Error && error.message) || t('brandFont.replaceFailed'),
                  ),
                )
            }
            isReplacing={fontReplace.isReplacing}
            onRequestDelete={() =>
              setPendingDelete({ mediaIds: [], fontIds: [openedFont.id], surface: 'detail_panel' })
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/** What a person is being asked to confirm, and which control asked. */
interface DeleteRequest {
  mediaIds: readonly string[];
  fontIds: readonly string[];
  surface: 'bulk_bar' | 'detail_panel';
}
